import type { OrderApi } from "../../api/OrderApi";
import type { BalanceFetchOptions } from "../../generated/dto/BalanceFetchOptions";
import type { BalanceStaff } from "../../generated/dto/BalanceStaff";
import type { BalanceTransactionFetchOptions } from "../../generated/dto/BalanceTransactionFetchOptions";
import type { BalanceTransactionSearchParams } from "../../generated/dto/BalanceTransactionSearchParams";
import type { BalanceTransactionStaff } from "../../generated/dto/BalanceTransactionStaff";
import type { BalanceTransactionStaffSearchResult } from "../../generated/dto/BalanceTransactionStaffSearchResult";
import type { CardFetchOptions } from "../../generated/dto/CardFetchOptions";
import type { CardStaff } from "../../generated/dto/CardStaff";
import type { CatalogDiscountInstanceFetchOptions } from "../../generated/dto/CatalogDiscountInstanceFetchOptions";
import type { CatalogDiscountInstanceStaff } from "../../generated/dto/CatalogDiscountInstanceStaff";
import type { CatalogItemInstanceFetchOptions } from "../../generated/dto/CatalogItemInstanceFetchOptions";
import type { CatalogItemInstanceStaff } from "../../generated/dto/CatalogItemInstanceStaff";
import type { CatalogTaxFetchOptions } from "../../generated/dto/CatalogTaxFetchOptions";
import type { CatalogTaxStaff } from "../../generated/dto/CatalogTaxStaff";
import type { CheckoutReadiness } from "../../generated/dto/CheckoutReadiness";
import type { DamageWaiverFetchOptions } from "../../generated/dto/DamageWaiverFetchOptions";
import type { DamageWaiverStaff } from "../../generated/dto/DamageWaiverStaff";
import type { DiscountCandidate } from "../../generated/dto/DiscountCandidate";
import type { DonationFetchOptions } from "../../generated/dto/DonationFetchOptions";
import type { DonationStaff } from "../../generated/dto/DonationStaff";
import type { GuestPrivilegeFetchOptions } from "../../generated/dto/GuestPrivilegeFetchOptions";
import type { GuestPrivilegeStaff } from "../../generated/dto/GuestPrivilegeStaff";
import type { LineItemDiscountInstanceFetchOptions } from "../../generated/dto/LineItemDiscountInstanceFetchOptions";
import type { LineItemDiscountInstanceStaff } from "../../generated/dto/LineItemDiscountInstanceStaff";
import type { LineItemFetchOptions } from "../../generated/dto/LineItemFetchOptions";
import type { LineItemRef } from "../../generated/dto/LineItemRef";
import type { LineItemRefundFetchOptions } from "../../generated/dto/LineItemRefundFetchOptions";
import type { LineItemRefundStaff } from "../../generated/dto/LineItemRefundStaff";
import type { LineItemRefundTaxFetchOptions } from "../../generated/dto/LineItemRefundTaxFetchOptions";
import type { LineItemRefundTaxStaff } from "../../generated/dto/LineItemRefundTaxStaff";
import type { LineItemStaff } from "../../generated/dto/LineItemStaff";
import type { LineItemTaxFetchOptions } from "../../generated/dto/LineItemTaxFetchOptions";
import type { LineItemTaxStaff } from "../../generated/dto/LineItemTaxStaff";
import type { MembershipStaff } from "../../generated/dto/MembershipStaff";
import type { MembershipStaffFetchOptions } from "../../generated/dto/MembershipStaffFetchOptions";
import type { OrderAddPurchaseRequest } from "../../generated/dto/OrderAddPurchaseRequest";
import type { OrderAddToCartRequest } from "../../generated/dto/OrderAddToCartRequest";
import type { OrderApplyDiscountFromCodeRequest } from "../../generated/dto/OrderApplyDiscountFromCodeRequest";
import type { OrderApplyDiscountToLineItemsRequest } from "../../generated/dto/OrderApplyDiscountToLineItemsRequest";
import type { OrderChangeBuyerRequest } from "../../generated/dto/OrderChangeBuyerRequest";
import type { OrderConfigureDeliveryRequest } from "../../generated/dto/OrderConfigureDeliveryRequest";
import type { OrderConfigureReceiptDeliveryRequest } from "../../generated/dto/OrderConfigureReceiptDeliveryRequest";
import type { OrderFetchOptions } from "../../generated/dto/OrderFetchOptions";
import type { OrderLineDetail } from "../../generated/dto/OrderLineDetail";
import type { OrderMember } from "../../generated/dto/OrderMember";
import type { OrderOpenOrderRequest } from "../../generated/dto/OrderOpenOrderRequest";
import type { OrderPaymentFetchOptions } from "../../generated/dto/OrderPaymentFetchOptions";
import type { OrderPaymentStaff } from "../../generated/dto/OrderPaymentStaff";
import type { OrderRef } from "../../generated/dto/OrderRef";
import type { OrderRefundFetchOptions } from "../../generated/dto/OrderRefundFetchOptions";
import type { OrderRefundStaff } from "../../generated/dto/OrderRefundStaff";
import type { OrderSearchParams } from "../../generated/dto/OrderSearchParams";
import type { OrderStaff } from "../../generated/dto/OrderStaff";
import type { OrderStaffSearchResult } from "../../generated/dto/OrderStaffSearchResult";
import type { OrderSwitchOrderSourceRequest } from "../../generated/dto/OrderSwitchOrderSourceRequest";
import type { OrderUnapplyDiscountFromLineItemsRequest } from "../../generated/dto/OrderUnapplyDiscountFromLineItemsRequest";
import type { OrderUnapplyLineItemDiscountRequest } from "../../generated/dto/OrderUnapplyLineItemDiscountRequest";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonRef } from "../../generated/dto/PersonRef";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { PurchaseDeliveryFetchOptions } from "../../generated/dto/PurchaseDeliveryFetchOptions";
import type { PurchaseDeliveryRef } from "../../generated/dto/PurchaseDeliveryRef";
import type { PurchaseDeliveryStaff } from "../../generated/dto/PurchaseDeliveryStaff";
import type { StockChangeFetchOptions } from "../../generated/dto/StockChangeFetchOptions";
import type { StockChangeStaff } from "../../generated/dto/StockChangeStaff";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class OrderFrontendService {
  constructor(private readonly api: OrderApi) {}
  async getOrderStaff(id: number, options?: OrderFetchOptions): Promise<OrderStaff> {
    return this.api.getOrderStaff(id, options)
  }
  async getLineItemStaff(id: number, options?: LineItemFetchOptions): Promise<LineItemStaff> {
    return this.api.getLineItemStaff(id, options)
  }
  async getLineItemDiscountInstanceStaff(id: number, options?: LineItemDiscountInstanceFetchOptions): Promise<LineItemDiscountInstanceStaff> {
    return this.api.getLineItemDiscountInstanceStaff(id, options)
  }
  async getBalanceTransactionStaff(id: number, options?: BalanceTransactionFetchOptions): Promise<BalanceTransactionStaff> {
    return this.api.getBalanceTransactionStaff(id, options)
  }
  async getLineItemTaxStaff(id: number, options?: LineItemTaxFetchOptions): Promise<LineItemTaxStaff> {
    return this.api.getLineItemTaxStaff(id, options)
  }
  async getLineItemRefundStaff(id: number, options?: LineItemRefundFetchOptions): Promise<LineItemRefundStaff> {
    return this.api.getLineItemRefundStaff(id, options)
  }
  async getLineItemRefundTaxStaff(id: number, options?: LineItemRefundTaxFetchOptions): Promise<LineItemRefundTaxStaff> {
    return this.api.getLineItemRefundTaxStaff(id, options)
  }
  async searchOrderStaff(params: OrderSearchParams, page: number, perPage: number): Promise<PaginationResult<OrderStaffSearchResult>> {
    return this.api.searchOrderStaff(params, page, perPage)
  }
  async searchBalanceTransactionStaff(params: BalanceTransactionSearchParams, page: number, perPage: number): Promise<PaginationResult<BalanceTransactionStaffSearchResult>> {
    return this.api.searchBalanceTransactionStaff(params, page, perPage)
  }
  async searchOrderStaffFull(params: OrderSearchParams, page: number, perPage: number, options?: OrderFetchOptions): Promise<PaginationResult<OrderStaff>> {
    return this.api.searchOrderStaffFull(params, page, perPage, options)
  }
  async searchBalanceTransactionStaffFull(params: BalanceTransactionSearchParams, page: number, perPage: number, options?: BalanceTransactionFetchOptions): Promise<PaginationResult<BalanceTransactionStaff>> {
    return this.api.searchBalanceTransactionStaffFull(params, page, perPage, options)
  }
  async getOrderStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    return this.api.getOrderStaffPersonId(id, options)
  }
  async getOrderStaffLineItems(id: number, options?: LineItemFetchOptions): Promise<Array<LineItemStaff>> {
    return this.api.getOrderStaffLineItems(id, options)
  }
  async getOrderStaffOrderRefunds(id: number, options?: OrderRefundFetchOptions): Promise<Array<OrderRefundStaff>> {
    return this.api.getOrderStaffOrderRefunds(id, options)
  }
  async getOrderStaffPayments(id: number, options?: OrderPaymentFetchOptions): Promise<Array<OrderPaymentStaff>> {
    return this.api.getOrderStaffPayments(id, options)
  }
  async getLineItemStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    return this.api.getLineItemStaffPersonId(id, options)
  }
  async getLineItemStaffDonation(id: number, options?: DonationFetchOptions): Promise<DonationStaff | null> {
    return this.api.getLineItemStaffDonation(id, options)
  }
  async getLineItemStaffMembership(id: number, options?: MembershipStaffFetchOptions): Promise<MembershipStaff | null> {
    return this.api.getLineItemStaffMembership(id, options)
  }
  async getLineItemStaffBalanceTransaction(id: number, options?: BalanceTransactionFetchOptions): Promise<BalanceTransactionStaff | null> {
    return this.api.getLineItemStaffBalanceTransaction(id, options)
  }
  async getLineItemStaffStockChange(id: number, options?: StockChangeFetchOptions): Promise<StockChangeStaff | null> {
    return this.api.getLineItemStaffStockChange(id, options)
  }
  async getLineItemStaffCard(id: number, options?: CardFetchOptions): Promise<CardStaff | null> {
    return this.api.getLineItemStaffCard(id, options)
  }
  async getLineItemStaffGuestPrivs(id: number, options?: GuestPrivilegeFetchOptions): Promise<GuestPrivilegeStaff | null> {
    return this.api.getLineItemStaffGuestPrivs(id, options)
  }
  async getLineItemStaffDamageWaiver(id: number, options?: DamageWaiverFetchOptions): Promise<DamageWaiverStaff | null> {
    return this.api.getLineItemStaffDamageWaiver(id, options)
  }
  async getLineItemStaffPurchaseDelivery(id: number, options?: PurchaseDeliveryFetchOptions): Promise<PurchaseDeliveryStaff | null> {
    return this.api.getLineItemStaffPurchaseDelivery(id, options)
  }
  async getLineItemStaffOrderId(id: number, options?: OrderFetchOptions): Promise<OrderStaff> {
    return this.api.getLineItemStaffOrderId(id, options)
  }
  async getLineItemStaffItemInstanceId(id: number, options?: CatalogItemInstanceFetchOptions): Promise<CatalogItemInstanceStaff> {
    return this.api.getLineItemStaffItemInstanceId(id, options)
  }
  async getLineItemStaffParentItemId(id: number, options?: LineItemFetchOptions): Promise<LineItemStaff | null> {
    return this.api.getLineItemStaffParentItemId(id, options)
  }
  async getLineItemStaffChildItems(id: number, options?: LineItemFetchOptions): Promise<Array<LineItemStaff>> {
    return this.api.getLineItemStaffChildItems(id, options)
  }
  async getLineItemStaffDiscountInstances(id: number, options?: LineItemDiscountInstanceFetchOptions): Promise<Array<LineItemDiscountInstanceStaff>> {
    return this.api.getLineItemStaffDiscountInstances(id, options)
  }
  async getLineItemStaffTaxes(id: number, options?: LineItemTaxFetchOptions): Promise<Array<LineItemTaxStaff>> {
    return this.api.getLineItemStaffTaxes(id, options)
  }
  async getLineItemStaffRefunds(id: number, options?: LineItemRefundFetchOptions): Promise<Array<LineItemRefundStaff>> {
    return this.api.getLineItemStaffRefunds(id, options)
  }
  async getLineItemDiscountInstanceStaffLineItemId(id: number, options?: LineItemFetchOptions): Promise<LineItemStaff> {
    return this.api.getLineItemDiscountInstanceStaffLineItemId(id, options)
  }
  async getLineItemDiscountInstanceStaffDiscountInstanceId(id: number, options?: CatalogDiscountInstanceFetchOptions): Promise<CatalogDiscountInstanceStaff> {
    return this.api.getLineItemDiscountInstanceStaffDiscountInstanceId(id, options)
  }
  async getBalanceTransactionStaffBalanceId(id: number, options?: BalanceFetchOptions): Promise<BalanceStaff> {
    return this.api.getBalanceTransactionStaffBalanceId(id, options)
  }
  async getLineItemTaxStaffLineItemId(id: number, options?: LineItemFetchOptions): Promise<LineItemStaff> {
    return this.api.getLineItemTaxStaffLineItemId(id, options)
  }
  async getLineItemTaxStaffTaxId(id: number, options?: CatalogTaxFetchOptions): Promise<CatalogTaxStaff> {
    return this.api.getLineItemTaxStaffTaxId(id, options)
  }
  async getLineItemRefundStaffLineItemId(id: number, options?: LineItemFetchOptions): Promise<LineItemStaff | null> {
    return this.api.getLineItemRefundStaffLineItemId(id, options)
  }
  async getLineItemRefundStaffTaxes(id: number, options?: LineItemRefundTaxFetchOptions): Promise<Array<LineItemRefundTaxStaff>> {
    return this.api.getLineItemRefundStaffTaxes(id, options)
  }
  async getLineItemRefundStaffOrderRefundId(id: number, options?: OrderRefundFetchOptions): Promise<OrderRefundStaff | null> {
    return this.api.getLineItemRefundStaffOrderRefundId(id, options)
  }
  async getLineItemRefundTaxStaffLineItemRefundId(id: number, options?: LineItemRefundFetchOptions): Promise<LineItemRefundStaff | null> {
    return this.api.getLineItemRefundTaxStaffLineItemRefundId(id, options)
  }
  async getLineItemRefundTaxStaffTaxId(id: number, options?: LineItemTaxFetchOptions): Promise<LineItemTaxStaff> {
    return this.api.getLineItemRefundTaxStaffTaxId(id, options)
  }
  async addPurchaseMember(request: OrderAddPurchaseRequest): Promise<LineItemRef> {
    return this.api.addPurchaseMember(request)
  }
  async addToCartStaff(request: OrderAddToCartRequest): Promise<LineItemRef> {
    return this.api.addToCartStaff(request)
  }
  async addToCartMember(request: OrderAddToCartRequest): Promise<LineItemRef> {
    return this.api.addToCartMember(request)
  }
  async applyDiscountFromCodeStaff(request: OrderApplyDiscountFromCodeRequest): Promise<void> {
    return this.api.applyDiscountFromCodeStaff(request)
  }
  async applyDiscountFromCodeMember(request: OrderApplyDiscountFromCodeRequest): Promise<void> {
    return this.api.applyDiscountFromCodeMember(request)
  }
  async applyDiscountToLineItemsStaff(request: OrderApplyDiscountToLineItemsRequest): Promise<void> {
    return this.api.applyDiscountToLineItemsStaff(request)
  }
  async cancelStaff(order: OrderRef): Promise<void> {
    return this.api.cancelStaff(order)
  }
  async cancelMember(order: OrderRef): Promise<void> {
    return this.api.cancelMember(order)
  }
  async changeBuyerStaff(request: OrderChangeBuyerRequest): Promise<OrderRef> {
    return this.api.changeBuyerStaff(request)
  }
  async checkoutOrderMember(order: OrderRef): Promise<OrderMember> {
    return this.api.checkoutOrderMember(order)
  }
  async checkoutReadinessMember(order: OrderRef): Promise<CheckoutReadiness> {
    return this.api.checkoutReadinessMember(order)
  }
  async configureDeliveryStaff(request: OrderConfigureDeliveryRequest): Promise<PurchaseDeliveryRef> {
    return this.api.configureDeliveryStaff(request)
  }
  async configureDeliveryMember(request: OrderConfigureDeliveryRequest): Promise<PurchaseDeliveryRef> {
    return this.api.configureDeliveryMember(request)
  }
  async configureReceiptDeliveryStaff(request: OrderConfigureReceiptDeliveryRequest): Promise<void> {
    return this.api.configureReceiptDeliveryStaff(request)
  }
  async confirmDeliveryStaff(lineItem: LineItemRef): Promise<void> {
    return this.api.confirmDeliveryStaff(lineItem)
  }
  async eligibleDiscountsStaff(order: OrderRef): Promise<Array<DiscountCandidate>> {
    return this.api.eligibleDiscountsStaff(order)
  }
  async emailReceiptStaff(order: OrderRef): Promise<void> {
    return this.api.emailReceiptStaff(order)
  }
  async getOpenOrderMember(): Promise<OrderRef> {
    return this.api.getOpenOrderMember()
  }
  async openOrderStaff(request: OrderOpenOrderRequest): Promise<OrderRef> {
    return this.api.openOrderStaff(request)
  }
  async orderLineDetailsStaff(order: OrderRef): Promise<Array<OrderLineDetail>> {
    return this.api.orderLineDetailsStaff(order)
  }
  async orderLineDetailsMember(order: OrderRef): Promise<Array<OrderLineDetail>> {
    return this.api.orderLineDetailsMember(order)
  }
  async payStaff(order: OrderRef): Promise<void> {
    return this.api.payStaff(order)
  }
  async payMember(order: OrderRef): Promise<void> {
    return this.api.payMember(order)
  }
  async removeFromCartStaff(lineItem: LineItemRef): Promise<void> {
    return this.api.removeFromCartStaff(lineItem)
  }
  async removeFromCartMember(lineItem: LineItemRef): Promise<void> {
    return this.api.removeFromCartMember(lineItem)
  }
  async reopenStaff(order: OrderRef): Promise<void> {
    return this.api.reopenStaff(order)
  }
  async reopenMember(order: OrderRef): Promise<void> {
    return this.api.reopenMember(order)
  }
  async retryDeliveriesStaff(order: OrderRef): Promise<void> {
    return this.api.retryDeliveriesStaff(order)
  }
  async retryReceiptDeliveryStaff(order: OrderRef): Promise<void> {
    return this.api.retryReceiptDeliveryStaff(order)
  }
  async startCartStaff(person: PersonRef | null): Promise<OrderRef> {
    return this.api.startCartStaff(person)
  }
  async switchOrderSourceStaff(request: OrderSwitchOrderSourceRequest): Promise<OrderRef> {
    return this.api.switchOrderSourceStaff(request)
  }
  async unapplyDiscountFromLineItemsStaff(request: OrderUnapplyDiscountFromLineItemsRequest): Promise<void> {
    return this.api.unapplyDiscountFromLineItemsStaff(request)
  }
  async unapplyLineItemDiscountStaff(request: OrderUnapplyLineItemDiscountRequest): Promise<void> {
    return this.api.unapplyLineItemDiscountStaff(request)
  }
}
